import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';

import MarkerIcon from '../../atoms/MarkerIcon';
import CloseButton from '../../atoms/CloseButton';
import RefreshButton from '../../atoms/RefreshButton';
import UserCardAvatar from '../../molecules/UserCardAvatar';
import ScrollArea from '../../atoms/ScrollArea';
import LoadingSpinner from '../../atoms/LoadingSpinner';
import {
    getMarkerTypeConfig,
    hasMarkerAvatarData,
    ICON_MODES,
    normalizeMarkerType,
} from '../../../utils/markerTypeConfig';
import { extractMetadataEntries } from '../../../utils/mapPopupModel';
import { sanitizeMarkerColor } from '../../../utils/mapMarkerPresentation';
import { formatCommitmentSummary } from '../../../utils/commitment';
import { formatInstrumentExperienceDisplay } from '../../../utils/instrumentExperience';
import { formatSeekingSummary } from '../../../utils/seeking';
import { humanizeMetadataKey } from '../../molecules/MapPopup';
import './DiscoverMarkerTablePanel.scss';

const UNKNOWN_VALUE = '—';
const TABLE_TABS = Object.freeze({
    ALL: 'all',
    SELECTED: 'selected',
});

const safeText = (value) => {
    if (typeof value !== 'string') {
        return '';
    }
    return value.replace(/[\r\n\t]/g, ' ').trim();
};

function resolveVisibilityLabel(value, visibilityLabels) {
    const normalized = typeof value === 'string' ? value.trim().toUpperCase() : '';
    if (visibilityLabels[normalized]) {
        return visibilityLabels[normalized];
    }
    return visibilityLabels.PUBLIC || UNKNOWN_VALUE;
}

function resolveAvatar(marker, metadata, config, author) {
    const authorUsername = safeText(
        metadata?.username
        || metadata?.displayName
        || metadata?.authorName
        || marker?.authorName,
    );
    return config.extractAvatar(marker, metadata, author, authorUsername);
}

function formatCreated(value, formatDateTime) {
    if (!value || typeof formatDateTime !== 'function') {
        return '';
    }
    try {
        return formatDateTime(value);
    } catch (error) {
        console.error('Failed to format date value for marker table', error);
        return '';
    }
}

export default function DiscoverMarkerTablePanel({
    t,
    isOpen,
    markers = [],
    markerColors = undefined,
    selectedIds = [],
    onSelectionChange = undefined,
    onRefresh = undefined,
    isLoading = false,
    onClose = undefined,
    formatDateTime = undefined,
    errorMessage = '',
    onOpenScheduledDetails = undefined,
}) {
    const [activeTab, setActiveTab] = useState(TABLE_TABS.ALL);
    const latestOnRefreshRef = useRef(onRefresh);
    const hasAutoRefreshedOpenRef = useRef(false);
    const visibilityLabels = useMemo(() => ({
        PUBLIC: t('map.popup.visibility.public', 'Public'),
        FRIENDS: t('map.popup.visibility.friends', 'Friends only'),
        ONLY_ME: t('map.popup.visibility.onlyMe', 'Only me'),
        PRIVATE: t('map.popup.visibility.private', 'Private'),
    }), [t]);

    const metadataFormatters = useMemo(() => ({
        commitment: (value) => formatCommitmentSummary(value, t),
        instrumentExperience: formatInstrumentExperienceDisplay,
        seeking: (value) => formatSeekingSummary(value, { t }),
    }), [t]);

    useEffect(() => {
        latestOnRefreshRef.current = onRefresh;
    }, [onRefresh]);

    useEffect(() => {
        if (!isOpen) {
            hasAutoRefreshedOpenRef.current = false;
            return;
        }
        if (hasAutoRefreshedOpenRef.current || typeof latestOnRefreshRef.current !== 'function') {
            return;
        }
        hasAutoRefreshedOpenRef.current = true;
        latestOnRefreshRef.current().catch((err) => {
            console.error('Failed to refresh marker table data', err);
        });
    }, [isOpen]);

    const tableRows = useMemo(() => {
        if (!Array.isArray(markers) || markers.length === 0) {
            return [];
        }

        return markers.map((marker) => {
            const normalizedType = normalizeMarkerType(marker.type);
            const config = getMarkerTypeConfig(normalizedType);
            const popupOverrides = marker._popupOverrides;
            const effectivePriorityFields = popupOverrides?.priorityMetadataFields
                ?? config.priorityMetadataFields;
            const metadataEntries = extractMetadataEntries(
                marker.metadata,
                effectivePriorityFields,
                {
                    formatters: metadataFormatters,
                    onlyPriority: Boolean(popupOverrides),
                    hideEmpty: popupOverrides ? popupOverrides.hideEmpty : true,
                },
            );
            const author = safeText(marker.authorName);
            const resolvedVisibility = resolveVisibilityLabel(marker.privacy, visibilityLabels);
            const created = formatCreated(marker.createdAt, formatDateTime);
            const avatar = resolveAvatar(marker, marker.metadata, config, author);
            const color = sanitizeMarkerColor(markerColors?.[normalizedType]);

            return {
                id: marker.id,
                marker,
                type: normalizedType,
                typeLabel: config.label,
                name: marker.name || config.label,
                author: author || UNKNOWN_VALUE,
                visibility: resolvedVisibility || UNKNOWN_VALUE,
                created: created || UNKNOWN_VALUE,
                metadataEntries,
                iconMode: config.iconMode,
                avatar,
                hasAvatar: hasMarkerAvatarData(avatar),
                markerColor: color,
            };
        });
    }, [formatDateTime, markerColors, markers, metadataFormatters, visibilityLabels]);

    const selectedSet = useMemo(
        () => new Set((selectedIds || []).map((id) => String(id))),
        [selectedIds],
    );
    const displayedRows = useMemo(() => {
        if (activeTab === TABLE_TABS.SELECTED) {
            return tableRows.filter((row) => selectedSet.has(String(row.id)));
        }
        return tableRows;
    }, [activeTab, selectedSet, tableRows]);
    const selectAllRef = useRef(null);

    const emitSelection = useCallback((ids) => {
        if (typeof onSelectionChange !== 'function') {
            return;
        }
        const markerIndex = new Map(
            tableRows.map((row) => [String(row.id), row.marker]),
        );
        const normalizedIds = (ids || []).map((id) => String(id));
        const validIds = normalizedIds.filter((id) => markerIndex.has(id));
        const markersPayload = validIds.map((id) => markerIndex.get(id));
        onSelectionChange({ ids: validIds, markers: markersPayload });
    }, [onSelectionChange, tableRows]);

    const handleToggleAll = useCallback(() => {
        const allIds = displayedRows.map((row) => String(row.id));
        const isAllSelected = allIds.length > 0 && allIds.every((id) => selectedSet.has(id));
        emitSelection(isAllSelected ? [] : allIds);
    }, [displayedRows, emitSelection, selectedSet]);

    const handleToggleRow = useCallback((id) => {
        const normalizedId = String(id);
        const next = new Set(selectedSet);
        if (next.has(normalizedId)) {
            next.delete(normalizedId);
        } else {
            next.add(normalizedId);
        }
        emitSelection(Array.from(next));
    }, [emitSelection, selectedSet]);

    const allIds = displayedRows.map((row) => String(row.id));
    const allSelected = allIds.length > 0 && allIds.every((id) => selectedSet.has(id));
    const partiallySelected = allIds.some((id) => selectedSet.has(id)) && !allSelected;

    const metadataColumns = useMemo(() => {
        const columns = [];
        displayedRows.forEach((row) => {
            row.metadataEntries.forEach(([key]) => {
                if (key && !columns.includes(key)) {
                    columns.push(key);
                }
            });
        });
        return columns;
    }, [displayedRows]);

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = partiallySelected;
        }
    }, [partiallySelected]);

    if (!isOpen) {
        return null;
    }

    return (
        <div className="discover-table-panel" role="dialog" aria-label={t('discoverPage.table.aria', 'Marker table view')}>
            <div className="discover-table-panel__header">
                <div className="discover-table-panel__heading">
                    <p className="discover-table-panel__eyebrow">
                        {activeTab === TABLE_TABS.SELECTED
                            ? t('discoverPage.table.subtitleSelected', 'Selected markers')
                            : t('discoverPage.table.subtitleAll', 'All markers')}
                    </p>
                    <h3 className="discover-table-panel__title">
                        {t('discoverPage.table.title', 'Table view')}
                    </h3>
                </div>
                <div className="discover-table-panel__header-controls">
                    <div
                        className="discover-table-panel__tabs"
                        role="tablist"
                        aria-label={t('discoverPage.table.tabsAria', 'Marker table filters')}
                    >
                        <button
                            type="button"
                            role="tab"
                            className={`discover-table-panel__tab${activeTab === TABLE_TABS.ALL ? ' is-active' : ''}`}
                            aria-selected={activeTab === TABLE_TABS.ALL}
                            onClick={() => setActiveTab(TABLE_TABS.ALL)}
                        >
                            {t('discoverPage.table.tabs.all', 'All Markers')}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            className={`discover-table-panel__tab${activeTab === TABLE_TABS.SELECTED ? ' is-active' : ''}`}
                            aria-selected={activeTab === TABLE_TABS.SELECTED}
                            onClick={() => setActiveTab(TABLE_TABS.SELECTED)}
                        >
                            {t('discoverPage.table.tabs.selected', 'Selected Markers')}
                        </button>
                    </div>
                    <div className="discover-table-panel__actions">
                        <RefreshButton
                            onClick={onRefresh}
                            className="discover-table-panel__refresh"
                            ariaLabel={t('discoverPage.table.refresh', 'Refresh table')}
                            title={t('discoverPage.table.refresh', 'Refresh table')}
                            disabled={isLoading}
                        />
                        <CloseButton
                            className="discover-table-panel__close"
                            onClick={onClose}
                            ariaLabel={t('discoverPage.table.close', 'Close')}
                        />
                    </div>
                </div>
            </div>

            <div className="discover-table-panel__body">
                {isLoading && (
                    <div className="discover-table-panel__loading" aria-live="polite">
                        <LoadingSpinner />
                        <span>{t('discoverPage.table.loading', 'Loading markers…')}</span>
                    </div>
                )}

                {errorMessage && (
                    <p className="discover-table-panel__error" role="alert">
                        {errorMessage}
                    </p>
                )}

                {displayedRows.length === 0 && !isLoading && (
                    <p className="discover-table-panel__empty">
                        {t('discoverPage.table.empty', 'No markers to display yet.')}
                    </p>
                )}

                {displayedRows.length > 0 && (
                    <ScrollArea className="discover-table-panel__scroll" ariaLabel={t('discoverPage.table.tableAria', 'Selected marker table')}>
                        <table className="discover-table-panel__table">
                            <thead>
                                <tr>
                                    <th scope="col">
                                        <input
                                            type="checkbox"
                                            aria-label={t('discoverPage.table.selectAll', 'Select all')}
                                            checked={allSelected}
                                            ref={selectAllRef}
                                            onChange={handleToggleAll}
                                        />
                                    </th>
                                    <th scope="col">{t('discoverPage.table.columns.type', 'Type')}</th>
                                    <th scope="col">{t('discoverPage.table.columns.name', 'Name')}</th>
                                    <th scope="col">{t('discoverPage.table.columns.author', 'Author')}</th>
                                    <th scope="col">{t('discoverPage.table.columns.visibility', 'Visibility')}</th>
                                    <th scope="col">{t('discoverPage.table.columns.created', 'Created')}</th>
                                    {metadataColumns.map((key) => (
                                        <th key={key} scope="col">{humanizeMetadataKey(key)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {displayedRows.map((row) => {
                                    const isRowSelected = selectedSet.has(String(row.id));
                                    const showAvatar = row.hasAvatar;
                                    const showMarkerIcon = row.iconMode === ICON_MODES.MARKER_ICON && !showAvatar;
                                    const canOpenScheduledDetails = typeof onOpenScheduledDetails === 'function'
                                        && (row.type === 'EVENT' || row.type === 'SESSION');
                                    const avatarUsername = row.avatar?.userId
                                        ? (row.avatar.username || row.author)
                                        : (row.avatar?.username || undefined);

                                    return (
                                        <tr key={row.id} className={isRowSelected ? 'is-selected' : ''}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    aria-label={t('discoverPage.table.selectRow', { name: row.name, defaultValue: 'Select row for {{name}}' })}
                                                    checked={isRowSelected}
                                                    onChange={() => handleToggleRow(row.id)}
                                                />
                                            </td>
                                            <td>
                                                <div className="discover-table-panel__type">
                                                    {showAvatar && (
                                                        <UserCardAvatar
                                                            userId={row.avatar.userId}
                                                            username={avatarUsername}
                                                            avatarUrl={row.avatar.avatarUrl}
                                                            alt={avatarUsername || row.author || row.typeLabel}
                                                            size="28px"
                                                            radius="30%"
                                                            enableProfilePreview={Boolean(row.avatar?.userId)}
                                                        />
                                                    )}
                                                    {showMarkerIcon && (
                                                        <MarkerIcon
                                                            color={row.markerColor}
                                                            size="small"
                                                            ariaLabel={t('discoverPage.table.typeIconAria', 'Marker type icon')}
                                                        />
                                                    )}
                                                    <span>{row.typeLabel}</span>
                                                </div>
                                            </td>
                                            <td>
                                                {canOpenScheduledDetails ? (
                                                    <button
                                                        type="button"
                                                        className="discover-table-panel__detail-link"
                                                        onClick={() => onOpenScheduledDetails(row.marker)}
                                                    >
                                                        {row.name}
                                                    </button>
                                                ) : row.name}
                                            </td>
                                            <td>{row.author}</td>
                                            <td>{row.visibility}</td>
                                            <td>{row.created}</td>
                                            {metadataColumns.map((key) => {
                                                const entry = row.metadataEntries.find(([metadataKey]) => metadataKey === key);
                                                const value = entry ? entry[1] : '';
                                                return (
                                                    <td key={`${row.id}-${key}`}>
                                                        {value != null && value !== '' ? String(value) : UNKNOWN_VALUE}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </ScrollArea>
                )}
            </div>
        </div>
    );
}

DiscoverMarkerTablePanel.propTypes = {
    t: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    markers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        type: PropTypes.string,
        metadata: PropTypes.object,
        authorName: PropTypes.string,
        createdAt: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.instanceOf(Date),
        ]),
        privacy: PropTypes.string,
    })),
    markerColors: PropTypes.objectOf(PropTypes.string),
    selectedIds: PropTypes.arrayOf(PropTypes.string),
    onSelectionChange: PropTypes.func,
    onRefresh: PropTypes.func,
    isLoading: PropTypes.bool,
    onClose: PropTypes.func,
    formatDateTime: PropTypes.func,
    errorMessage: PropTypes.string,
    onOpenScheduledDetails: PropTypes.func,
};
